import { FullNamePipe } from '../pipes';
import { getValue } from './get-value';

/*
    filterAndSortData does filtering and sorting by mimicing API endpoint call signature (i.e. '/api/data/?filter=cat&sort=-age)
    The reason for this is to quickly switch between client-side and server-side filter/sorting
*/
export function filterAndSortData<T>(
  data: T[],
  filters: { [prop: string]: string | number },
): T[] {
  let filteredData = [...data];

  Object.entries(filters).forEach(([prop, value]) => {
    if (prop === 'ordering') {
      if (typeof value === 'string') {
        filteredData = sortData(filteredData, value);
      }
    } else if (prop.endsWith('_high')) {
      filteredData = filteredData.filter(
        (row: any) => Number(row[prop.replace('_high', '')]) <= Number(value),
      );
    } else if (prop.endsWith('_low')) {
      filteredData = filteredData.filter(
        (row: any) => Number(row[prop.replace('_low', '')]) >= Number(value),
      );
    } else if (prop === 'name') {
      filteredData = filteredData.filter((row) => {
        const name = new FullNamePipe().transform(row);

        return comparePartialValues(name, String(value));
      });
    } else {
      filteredData = filteredData.filter((row: any) =>
        comparePartialValues(row[prop], String(value)),
      );
    }
  });

  return filteredData;
}

// Sort data ascending with normal prop, for descending prepend '-'
export function sortData<T>(data: T[], sortingProp: string): T[] {
  return data.sort((row1: any, row2: any) => {
    if (sortingProp[0] === '-') {
      return row1[sortingProp.slice(1)] < row2[sortingProp.slice(1)] ? 1 : -1;
    } else {
      return row1[sortingProp] > row2[sortingProp] ? 1 : -1;
    }
  });
}

export function compareExactValues(
  dataValue = '',
  filterValue: string,
): boolean {
  return !filterValue || dataValue.toLowerCase() === filterValue.toLowerCase();
}

export function comparePartialValues(
  dataValue = '',
  filterValue: string,
): boolean {
  return (
    !filterValue || dataValue.toLowerCase().includes(filterValue.toLowerCase())
  );
}

/**
 * Return a list of filtered items given list
 * @param list array of objects of type T to filter
 * @param searchTerm string to compare property values with
 * @param props object properties for comparison
 */
export function filterList<T>(
  list: T[],
  searchTerm: string,
  ...props: string[]
): T[] {
  const filteredList = [...list];

  return filteredList.filter((item) =>
    props.reduce((hasMatched: boolean, prop) => {
      const value = getValue(item, prop);

      return hasMatched || comparePartialValues(value, searchTerm);
    }, false),
  );
}

/**
 * Currying function whose return function will set flag on items given search term and comparison properties
 * By default, flagProperty=true for items that match, can override to set matching items with flagProperty=false
 * @param flagProperty property name for flag on list items
 * @param flip boolean to flip flag property value on matching items, default to false
 */
export function setFlagOnListItem<T>(
  flagProperty: string,
  flip = false,
): (
  list: T[],
  searchTerm: string,
  ...props: string[]
) => (T & { [key: string]: boolean })[] {
  return (
    list: T[],
    searchTerm: string,
    ...props: string[]
  ): (T & { [key: string]: boolean })[] =>
    list.map((item) => {
      const isMatch = props.reduce((hasMatched, prop) => {
        const value = getValue(item, prop);

        return hasMatched || comparePartialValues(value, searchTerm);
      }, false);

      return {
        ...item,
        [flagProperty]: flip ? !isMatch : isMatch,
      };
    });
}
